import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Modal, Typography, Rating, Paper, TextField } from "@mui/material";
import { AppFeedbackRequest } from '../../hooks/useGetCurrentUser';
import { customFetchV2 } from '../../services/custom-fetch-v2';
import { SlideComponent } from '../bits/SlideComponent';
import Button from '../bits/Button.jsx';

declare global {
  interface Window {
    // https://github.com/dpa99c/cordova-launch-review/blob/master/www/launchreview.js
    LaunchReview?: {
      isRatingSupported: () => boolean;

      rating: (
        success?: (result: any) => void,
        error?: (err: any) => void,
      ) => void;
    };
  }
}

const PopupContainer = styled(Paper)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 400px;
  padding: 24px 16px;
  border-radius: 12px;
  outline: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  
  h1 {
    font-size: 1.75rem;
    margin-bottom: 12px;
    font-weight: 600;
    text-align: center;
    color: hsl(0, 0%, 29%);
  }
  
  p {
    font-size: 1rem;
    margin-bottom: 8px;
    text-align: center;
    color: hsl(0, 0%, 29%)
  }
`

const StarsContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`

const FeedbackContainer = styled.div`
  margin: 8px 4px;
`

const DialogFooter = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
`

const ThankYouContainer = styled.div`
  text-align: center;
  padding: 24px 16px;
`

interface AppFeedbackModalProps {
  /* If an appFeedbackRequest is present then the modal is shown. */
  appFeedbackRequest?: AppFeedbackRequest;

  /**
  * Optional callback that's called when a user submits feedback.
  * Provides the rating and notes that were submitted.
  */
  onFeedbackSubmitted?: (rating: number, notes: string) => void;

  /* When user exits out of modal. */
  onCancel: () => void;
 }

 /**
  * If appFeedbackRequest is present, this modal will be shown to the user.
  */
const AppFeedbackModal: React.FC<AppFeedbackModalProps> = ({ appFeedbackRequest, onFeedbackSubmitted, onCancel }: AppFeedbackModalProps) => {
  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    console.log(`isRatingSupported: ${window.LaunchReview?.isRatingSupported()}`);

    // Reset state whenever this modal is hidden.
    if (!appFeedbackRequest) {
      setRating(null);
      setNotes("");
      setSubmitted(false);
    }
  }, [appFeedbackRequest])

  const handleRatingChange = (_event: React.SyntheticEvent, newValue: number | null) => {
    setRating(newValue)
  }

  const handleSubmit = () => {
    if (rating) {
      setSubmitted(true);

      if (appFeedbackRequest) {
        // Fire and forget request to update the user's feedback
        customFetchV2('/api/v2/users/me/app-feedback/responses', {
          method: 'POST',
          body: JSON.stringify({
            appFeedbackRequestId: appFeedbackRequest?.id,
            rating,
            notes,
          }),
        }).then(response => {
          if (!response.ok) {
            console.error('Unable to submit user feedback');
          }
        }).catch(error => {
          console.error('Unable to submit user feedback', error);
        });
        
        // If the rating is high (4 or 5), prompt for app store review after a few seconds
        if (rating >= 4 && window.LaunchReview?.isRatingSupported()) {
          setTimeout(() => {
            console.log('Making rating request')
            window.LaunchReview.rating(
              // status value varies by platform. I've seen it be 'shown' on IOS but I didn't see any confirmation
              // https://github.com/dpa99c/cordova-launch-review?tab=readme-ov-file#advanced-usage-1
              (status: any) => { console.log(`Rating callback status: ${status}`) },
              (err: any) => { console.log(`Rating error status: ${err}`) },
            );
          }, 2000);
        }
      }
    
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(rating, notes);
      }
    }
  }

  return (
    <Modal open={!!appFeedbackRequest} onClose={onCancel} aria-labelledby="app-review-modal">
      <PopupContainer>
        {!submitted ? (
          <>
            <h1>
              Give Feedback
            </h1>
            <p>
              Please rate your experience using ShieldGPS.
            </p>
            <StarsContainer>
              <Rating
                name="app-rating"
                size="large"
                value={rating}
                onChange={handleRatingChange}
                precision={1}
                sx={{
                  fontSize: "3rem",
                  "& .MuiRating-iconFilled": {
                    color: "#faaf00",
                  },
                  "& .MuiRating-iconHover": {
                    color: "#faaf00",
                  },
                }}
              />
            </StarsContainer>


              <SlideComponent in={rating !== null && rating < 5}>
                <FeedbackContainer>
                { /* Tried using label=.. but it was buggy on IOS. */ }
                  <TextField
                    id="description"
                    placeholder="How can we improve?"
                    value={notes}
                    margin="normal"
                    multiline
                    fullWidth
                    minRows={4}
                    maxRows={4}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </FeedbackContainer>
              </SlideComponent>

            <DialogFooter>
              <Button
                onClick={handleSubmit}
                disabled={rating === null}
                fullWidth
                color="primary">
                Submit
              </Button>
            </DialogFooter>
          </>
        ) : (
          <ThankYouContainer>
            <h1>
              Thanks for your feedback!
            </h1>
            <p>
              {rating === 5
                ? "We're glad you're enjoying the app!"
                : "We appreciate your suggestions and will work to improve the experience."}
            </p>
          </ThankYouContainer>
        )}
      </PopupContainer>
    </Modal>
  )
}

export { AppFeedbackModal }
